import type { CommunitySummary } from '@/features/communities/api/communities.api'
import type { ThoughtContent } from '@/features/thought/lib/thought'
import { request } from '@/shared/api/request'
import { camelize } from '@/shared/lib/camelize'

export interface UserWallet {
  balance?: number | null
  incoming?: number | null
  outgoing?: number | null
  /** Nightly earnings awaiting the monthly settlement - visible, not spendable. */
  pending?: number | null
}

export interface ProfilePostSummary {
  id: string
  mediaType?: number | null
  mediaUrls?: string | null
  thumbnailUrl?: string | null
  likeCount?: number | null
  viewCount?: number | null
  /** Backend PostStatus: Created = 10, InProgress = 20, Published = 30. */
  status?: number | null
  /** Admin blocked the post - only the owner receives it (BLOCKED stamp). */
  isBlocked?: boolean | null
  /** Admin marked the post as fake news (FAKE stamp; creator can't delete). */
  isFakeNews?: boolean | null
  /** Set when the post was published in a community: the grid shows a "#slug" tag. */
  communityId?: string | null
  communitySlug?: string | null
  communityName?: string | null
  /** The card of a Thought post: the grid draws it instead of a thumbnail. */
  thought?: ThoughtContent | null
}

export function isVideoSummary(post: ProfilePostSummary): boolean {
  return post.mediaType === 20
}

/** Media is still being processed - no playable content yet. */
export function isProcessingSummary(post: ProfilePostSummary): boolean {
  return post.status != null && post.status < 30
}

export function summaryThumbnail(post: ProfilePostSummary): string | null {
  const first = firstMediaUrl(post)
  return isVideoSummary(post) ? (post.thumbnailUrl ?? first) : first
}

export function firstMediaUrl(post: ProfilePostSummary): string | null {
  const raw = post.mediaUrls
  if (!raw) return null
  try {
    const decoded: unknown = JSON.parse(raw)
    if (Array.isArray(decoded)) return (decoded[0] as string) ?? null
  } catch {
    /* bare URL */
  }
  return raw
}

export interface ProfilePageSummary {
  id: string
  username?: string | null
  name?: string | null
  avatarUrl?: string | null
  coverUrl?: string | null
  followersCount?: number | null
  postsCount?: number | null
  /** Admin blocked the page - only the owner's lists carry it (BLOCKED stamp). */
  isBlocked?: boolean | null
}

export interface PageDetails {
  id: string
  name?: string | null
  username?: string | null
  avatarUrl?: string | null
  coverUrl?: string | null
  bio?: string | null
  category?: string | null
  website?: string | null
  location?: string | null
  /** The page's country (ISO 3166-1 alpha-2), picked at creation. */
  countryCode?: string | null
  followersCount?: number | null
  postsCount?: number | null
  isVerified?: boolean | null
  isFollowing?: boolean | null
  isAdmin?: boolean | null
  /** Owning user - drives the "Managed by" card and the Message button. */
  ownerId?: string | null
  ownerUsername?: string | null
  ownerDisplayName?: string | null
  ownerAvatarUrl?: string | null
  ownerIsVerified?: boolean | null
  /** Admin blocked the page - the server only returns it to the owner (support banner). */
  isBlocked?: boolean | null
}

export interface UserProfile {
  id: string
  username: string
  displayName?: string | null
  avatarUrl?: string | null
  bio?: string | null
  externalLink?: string | null
  country?: string | null
  countryCode?: string | null
  followersCount?: number | null
  followingCount?: number | null
  postsCount?: number | null
  totalLikes?: number | null
  totalViews?: number | null
  isOwnProfile?: boolean | null
  isFollowing?: boolean | null
  inviteCode?: string | null
  wallet?: UserWallet | null
  posts?: ProfilePostSummary[] | null
  savedPosts?: ProfilePostSummary[] | null
  pages?: ProfilePageSummary[] | null
  /** Communities the user owns, biggest first: the header previews the first two. */
  communities?: CommunitySummary[] | null
  /** How many communities the user owns in total (the header shows "More" past two). */
  communitiesCount?: number | null
  /** Last request the user made, stamped every few minutes: the header's "last seen" line. */
  lastSeenAt?: string | null
  /** UserNature bit flags: 1 = Pioneer, 2 = Premium - drives the avatar badge. */
  nature?: number | null
  /** Admin-granted platform badge (users.is_eleva_verified on the backend). */
  isVerified?: boolean | null
  /** KYC approved AND published by the user - shows the green badge. */
  kycVerified?: boolean | null
  /** Own profile only - the settings "Public KYC badge" toggle. */
  isKycPublic?: boolean | null
  /** Own profile only - the avatar upload pipeline is still processing. */
  avatarProcessing?: boolean | null
  /** Own profile only - ISO date when the next avatar change unlocks; null = first change still free. */
  avatarChangeableAt?: string | null
  /** Own profile only - ISO date when the next username change unlocks; null = first change still free. */
  usernameChangeableAt?: string | null
  /** Own profile only - false when an admin switched off token earnings (profile shows a notice). */
  canEarnTokens?: boolean | null
  /** Own profile only - accepted-friend count (others never expose it). */
  friendsCount?: number | null
  /** Own profile only - who invited this account (renders the "Invited by" chip). */
  referrerId?: string | null
  referrerUsername?: string | null
  /** Other profiles only - my relationship: none | pending_sent | pending_received | friends. */
  friendStatus?: string | null
  /** Own profile only - backend-synced settings switches. */
  settings?: ProfilePrivacySettings | null
}

/** Profile "about me" entry (kind: 0 education, 1 work, 2 vacation, 3 other). */
export interface AboutItem {
  id: string
  kind: number
  name: string
  startYear?: number | null
  endYear?: number | null
}

/** Own-profile settings summary - only the fields the web app reads. */
export interface ProfilePrivacySettings {
  /** False = the "do not accept gifts" switch is on. */
  acceptsGifts?: boolean | null
  /** ProfileConnections: 1 everyone, 2 friends - 2 = "do not accept messages" on. */
  whoCanMessage?: number | null
  /**
   * FriendRequestPrivacy flags: 0 custom-none, 1 everyone, 2 friends of
   * friends, 4 people I follow, 8 the explicit "no one" switch.
   */
  friendRequestPrivacy?: number | null
  /** Social sanity: seconds the feed holds on an image / video post (0 = off). */
  imageWatchSeconds?: number | null
  videoWatchSeconds?: number | null
  /** The "show last seen" switch (two-way: off also hides everyone's from me). */
  showLastSeen?: boolean | null
  /** When that switch may be flipped again; null = free to flip now. */
  lastSeenChangeableAt?: string | null
}

/** GET /premium/status and the response of subscribe/cancel. */
export interface PremiumStatus {
  isActive: boolean
  /** PremiumMethod enum: 1 = App Store, 2 = tokens, 3 = card via Stripe (web), 4 = Google Play. */
  method?: number | null
  nextBillingAt?: string | null
  monthlyTokens?: number | null
  cancelAtPeriodEnd?: boolean | null
}

export const PREMIUM_METHOD = { store: 1, tokens: 2, stripe: 3, googlePlay: 4 } as const

/** Store-billed subscriptions can only be cancelled inside the mobile app. */
export function isStoreManagedPremium(status: PremiumStatus): boolean {
  return status.method === PREMIUM_METHOD.store || status.method === PREMIUM_METHOD.googlePlay
}

/**
 * Row from GET /wallet/transactions. `tokens` is signed - negative means the
 * tokens left this wallet. The counterparty fields are null for system
 * wallets (store top-ups etc.); `otherWalletType` labels those.
 */
export interface WalletTransactionEntry {
  id: string
  /** TransactionType enum: 10 gift, 20 settlement, 30 purchase, 100 other. */
  type?: number | null
  tokens?: number | null
  /** Earnings not yet settled - rows render a PENDING chip. */
  isPending?: boolean | null
  createdAt?: string | null
  otherUsername?: string | null
  otherAvatarUrl?: string | null
  otherWalletType?: number | null
}

export function isIncomingTransaction(tx: WalletTransactionEntry): boolean {
  return (tx.tokens ?? 0) >= 0
}

/** Entry in followers / following / blocked lists (loose field names). */
export interface FollowUser {
  id?: string | null
  userId?: string | null
  username?: string | null
  displayName?: string | null
  avatarUrl?: string | null
}

export function followUserId(user: FollowUser): string {
  return user.id ?? user.userId ?? user.username ?? ''
}

export function followUserLabel(user: FollowUser): string {
  return user.displayName || user.username || 'User'
}

export interface RankingEntry {
  rank: number
  userId?: string | null
  username?: string | null
  avatarUrl?: string | null
  score?: number | null
  isCurrentUser?: boolean | null
}

export interface ChartPoint {
  date?: string | null
  views?: number | null
  tokens?: number | null
  value?: number | null
}

export function chartAmount(point: ChartPoint): number {
  return point.views ?? point.tokens ?? point.value ?? 0
}

export interface AnalyticsData {
  viewsCurrent?: number | null
  viewsPrevious?: number | null
  likesCurrent?: number | null
  likesPrevious?: number | null
  commentsCurrent?: number | null
  commentsPrevious?: number | null
  sharesCurrent?: number | null
  sharesPrevious?: number | null
  tokensEarnedCurrent?: number | null
  tokensEarnedPrevious?: number | null
  tokensSpentCurrent?: number | null
  tokensAdsCurrent?: number | null
  tokensReferralCurrent?: number | null
  walletAvailable?: number | null
  walletPending?: number | null
  ambassadorReferralsCount?: number | null
  ambassadorEarningsTotal?: number | null
  postsTotal?: number | null
  postsVideo?: number | null
  postsPhoto?: number | null
  viewsChart?: ChartPoint[] | null
  tokensChart?: ChartPoint[] | null
}

export interface Referral {
  userId?: string | null
  username?: string | null
  displayName?: string | null
  avatarUrl?: string | null
  tokensEarned?: number | null
}

const PROFILE = '/api/eleva/profile'
const PAGES = '/api/eleva/pages'
const WALLET = '/api/eleva/wallet'

/**
 * GET /wallet/kyc/status - in_progress means a Didit verification session is
 * open (resumable); pending means the decision is with Didit or the staff;
 * expired flows start over without paying the fee again.
 */
export interface KycStatus {
  status: 'none' | 'pending' | 'in_progress' | 'approved' | 'rejected' | 'expired'
  /** False while the KycEnabled app setting is off. */
  available?: boolean | null
  balance?: number | null
  minTokens?: number | null
  feeTokens?: number | null
  eligible?: boolean | null
}

/**
 * Row from GET /wallet/withdrawals. `amountCents` is the fiat value finance
 * pays out manually; status walks pending -> approved -> paid (rejected
 * refunds the tokens).
 */
export interface WithdrawalEntry {
  id: string
  tokens?: number | null
  amountCents?: number | null
  status?: 'pending' | 'approved' | 'paid' | 'rejected' | string | null
  createdAt?: string | null
}

/**
 * Not seeded in app_settings, so the value never reaches clients - the same
 * 100-token floor the backend falls back to (MinWithdrawTokens).
 */
export const MIN_WITHDRAW_TOKENS = 100

export const walletApi = {
  /** Requests a manual payout; the server enforces KYC + minimum + balance. */
  async withdraw(input: { tokens?: number; all: boolean }): Promise<void> {
    await request({
      method: 'POST',
      url: `${WALLET}/withdraw`,
      data: { all: input.all, ...(input.all ? {} : { tokens: input.tokens }) },
    })
  },

  async getWithdrawals(): Promise<WithdrawalEntry[]> {
    const data = await request<unknown>({ url: `${WALLET}/withdrawals` })
    return camelize<WithdrawalEntry[]>(data)
  },
}

/** One Stripe price as the web shows it: amount in hundredths of the major unit. */
export interface StripePriceInfo {
  priceId: string
  amountCents: number
  currency: string
}

export interface StripeTokenPack extends StripePriceInfo {
  tokens: number
}

/** GET /payments/stripe/catalog - `available` is false while Stripe is off. */
export interface StripeCatalog {
  available: boolean
  black?: StripePriceInfo | null
  packages: StripeTokenPack[]
}

/** GET /payments/stripe/session/{id} - what became of a Checkout Session. */
export interface PaymentConfirmation {
  status: 'completed' | 'pending' | 'failed'
  kind: 'tokens' | 'premium'
  tokensAdded?: number | null
  balance?: number | null
  alreadyProcessed?: boolean | null
}

const PAYMENTS = '/api/eleva/payments'

/**
 * Card payments through Stripe Checkout (hosted page): the backend opens a
 * session and the browser is sent to its URL; Stripe brings the user back to
 * /payments/return, which confirms the session (idempotent with the webhook).
 */
export const paymentsApi = {
  async getCatalog(): Promise<StripeCatalog> {
    const data = await request<unknown>({ url: `${PAYMENTS}/stripe/catalog` })
    return camelize<StripeCatalog>(data)
  },

  /** Stripe Checkout for a token pack; returns the hosted payment URL. */
  async checkoutTokens(tokens: number): Promise<{ url: string; sessionId: string }> {
    const data = await request<unknown>({
      method: 'POST',
      url: `${PAYMENTS}/stripe/checkout`,
      data: { kind: 'tokens', tokens },
    })
    return camelize<{ url: string; sessionId: string }>(data)
  },

  /** Stripe Checkout for the monthly Eleva Black subscription. */
  async checkoutPremium(): Promise<{ url: string; sessionId: string }> {
    const data = await request<unknown>({
      method: 'POST',
      url: `${PAYMENTS}/stripe/checkout`,
      data: { kind: 'premium' },
    })
    return camelize<{ url: string; sessionId: string }>(data)
  },

  async confirmSession(sessionId: string): Promise<PaymentConfirmation> {
    const data = await request<unknown>({
      url: `${PAYMENTS}/stripe/session/${encodeURIComponent(sessionId)}`,
    })
    return camelize<PaymentConfirmation>(data)
  },

  /** Stripe Billing Portal (invoices, card on file) for card subscribers. */
  async openPortal(): Promise<{ url: string }> {
    const data = await request<unknown>({ method: 'POST', url: `${PAYMENTS}/stripe/portal` })
    return camelize<{ url: string }>(data)
  },
}

/** "$14.99" / "14,99 €" from a catalog amount, in the viewer's locale. */
export function formatMoney(amountCents: number, currency: string, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(amountCents / 100)
  } catch {
    return `${(amountCents / 100).toFixed(2)} ${currency}`
  }
}

export const kycApi = {
  async getStatus(): Promise<KycStatus> {
    const data = await request<unknown>({ url: `${WALLET}/kyc/status` })
    return camelize<KycStatus>(data)
  },

  /**
   * Charges the one-time fee (first start only) and opens a Didit
   * verification session, returning its hosted-flow url. Resuming an
   * in_progress flow mints a fresh session. `url` is absent when Didit
   * isn't configured - the request then queues for manual staff review.
   */
  async start(): Promise<{ status: string; url?: string | null }> {
    const data = await request<unknown>({ method: 'POST', url: `${WALLET}/kyc/start` })
    return camelize<{ status: string; url?: string | null }>(data)
  },
}

/**
 * The signed-in user's referral code, fetched once per session. Shared
 * profile links append it as ?ref= so a signed-out recipient registers with
 * it preset. Not every account has a code (the backend 400s then) - the miss
 * is cached too and links go out plain. Mirrors the mobile clients.
 */
let myReferralCode: Promise<string | null> | undefined

/** Snake-cased "about me" payload; optional years only ride when set. */
function aboutBody(input: {
  kind: number
  name: string
  startYear?: number | null
  endYear?: number | null
}): Record<string, unknown> {
  const body: Record<string, unknown> = { kind: input.kind, name: input.name }
  if (input.startYear != null) body.start_year = input.startYear
  if (input.endYear != null) body.end_year = input.endYear
  return body
}

export const profileApi = {
  async getUserProfile(userId: string): Promise<UserProfile> {
    const data = await request<unknown>({ url: `${PROFILE}/id/${userId.toLowerCase()}` })
    return camelize<UserProfile>(data)
  },

  getMyReferralCode(): Promise<string | null> {
    myReferralCode ??= request<{ code?: string | null }>({ url: '/api/eleva/referral-codes/my' })
      .then((data) => data.code ?? null)
      .catch(() => null)
    return myReferralCode
  },

  async updateProfile(input: {
    displayName?: string
    bio?: string
    externalLink?: string
  }): Promise<void> {
    const body: Record<string, string> = {}
    if (input.displayName !== undefined) body.display_name = input.displayName
    if (input.bio !== undefined) body.bio = input.bio
    if (input.externalLink !== undefined) body.external_link = input.externalLink
    if (Object.keys(body).length === 0) return
    await request({ method: 'PUT', url: `${PROFILE}/update`, data: body })
  },

  /** Sets the profile country (ISO 3166-1 alpha-2, picked in settings). */
  async updateCountry(code: string): Promise<void> {
    await request({ method: 'PUT', url: `${PROFILE}/country`, data: { country_code: code } })
  },

  /** Publishes/hides the "KYC verified" profile badge. */
  async setKycPublic(enabled: boolean): Promise<void> {
    await request({ method: 'POST', url: `${PROFILE}/kyc-public`, data: { enabled } })
  },

  /**
   * The "show last seen" privacy switch (two-way). The server locks it for
   * LastSeenToggleCooldownHours after every flip and answers
   * last_seen_change_cooldown (with the unlock time) while locked.
   */
  async setLastSeenVisibility(enabled: boolean): Promise<void> {
    await request({ method: 'POST', url: `${PROFILE}/last-seen-visibility`, data: { enabled } })
  },

  /** enabled=false is the "do not accept gifts" settings switch. */
  async setGiftsAcceptance(enabled: boolean): Promise<void> {
    await request({ method: 'POST', url: `${PROFILE}/gifts-acceptance`, data: { enabled } })
  },

  /** friendsOnly=true is the "do not accept messages" settings switch. */
  async setMessagePrivacy(friendsOnly: boolean): Promise<void> {
    await request({
      method: 'POST',
      url: `${PROFILE}/message-privacy`,
      data: { friends_only: friendsOnly },
    })
  },

  /**
   * Sets the "friend requests can be from" mask (FriendRequestPrivacy
   * flags). Returns the value the server stored - Everyone and the explicit
   * NoOne switch collapse to their pure form there.
   */
  /**
   * Social sanity dwell times (images 0-10 s, videos 0-30 s, 0 = off).
   * Returns the clamped values the server stored.
   */
  async setSocialSanity(
    imageSeconds: number,
    videoSeconds: number,
  ): Promise<{ image: number; video: number }> {
    const response = await request<{ image_watch_seconds?: number; video_watch_seconds?: number }>({
      method: 'POST',
      url: `${PROFILE}/social-sanity`,
      data: { image_seconds: imageSeconds, video_seconds: videoSeconds },
    })
    return {
      image: response.image_watch_seconds ?? imageSeconds,
      video: response.video_watch_seconds ?? videoSeconds,
    }
  },

  async setFriendRequestPrivacy(privacy: number): Promise<number> {
    const response = await request<{ friend_request_privacy?: number }>({
      method: 'POST',
      url: `${PROFILE}/friend-request-privacy`,
      data: { privacy },
    })
    return response.friend_request_privacy ?? privacy
  },

  // "About me" entries

  /** Public "about me" entries of any profile - the "More info" panel and
   *  the owner's editor fetch them on demand (they no longer ride in the
   *  profile response). */
  async getAboutItems(userId: string): Promise<AboutItem[]> {
    const response = await request<{ data?: unknown[] }>({
      url: `${PROFILE}/${userId.toLowerCase()}/about`,
    })
    return (response.data ?? []).map((row) => camelize<AboutItem>(row))
  },

  async addAboutItem(input: {
    kind: number
    name: string
    startYear?: number | null
    endYear?: number | null
  }): Promise<AboutItem> {
    const data = await request<unknown>({
      method: 'POST',
      url: `${PROFILE}/about`,
      data: aboutBody(input),
    })
    return camelize<AboutItem>(data)
  },

  async updateAboutItem(
    id: string,
    input: { kind: number; name: string; startYear?: number | null; endYear?: number | null },
  ): Promise<AboutItem> {
    const data = await request<unknown>({
      method: 'PUT',
      url: `${PROFILE}/about/${id.toLowerCase()}`,
      data: aboutBody(input),
    })
    return camelize<AboutItem>(data)
  },

  async deleteAboutItem(id: string): Promise<void> {
    await request({ method: 'DELETE', url: `${PROFILE}/about/${id.toLowerCase()}` })
  },

  /** Interests picked in settings, as PostMood ids. */
  async getInterests(): Promise<number[]> {
    const data = await request<Record<string, unknown>>({ url: `${PROFILE}/interests` })
    return Array.isArray(data?.preferred_moods) ? (data.preferred_moods as number[]) : []
  },

  async saveInterests(moods: number[]): Promise<void> {
    await request({
      method: 'POST',
      url: `${PROFILE}/interests`,
      data: { preferred_moods: moods },
    })
  },

  async getFollowers(userId: string): Promise<FollowUser[]> {
    const data = await request<unknown>({ url: `${PROFILE}/${userId}/followers` })
    return camelize<FollowUser[]>(data)
  },

  async getFollowing(userId: string): Promise<FollowUser[]> {
    const data = await request<unknown>({ url: `${PROFILE}/${userId}/following` })
    return camelize<FollowUser[]>(data)
  },

  async removeFollower(userId: string): Promise<void> {
    await request({ method: 'DELETE', url: `${PROFILE}/follower/${userId}` })
  },

  /** Unfollow = the same follow toggle endpoint. */
  async unfollow(userId: string): Promise<void> {
    await request({ method: 'POST', url: `${PROFILE}/follow/${userId}` })
  },

  async getBlockedUsers(): Promise<FollowUser[]> {
    const data = await request<unknown>({ url: `${PROFILE}/blocked` })
    return camelize<FollowUser[]>(data)
  },

  async unblockUser(userId: string): Promise<void> {
    await request({ method: 'DELETE', url: `${PROFILE}/block/${userId}` })
  },

  // ---- Premium ----

  async getPremiumStatus(): Promise<PremiumStatus> {
    const data = await request<unknown>({ url: '/api/eleva/premium/status' })
    return camelize<PremiumStatus>(data)
  },

  /** `method`: 1 = store, 2 = tokens. Throws when the wallet can't cover the first month. */
  async subscribePremium(method: number): Promise<PremiumStatus> {
    const data = await request<unknown>({
      method: 'POST',
      url: '/api/eleva/premium/subscribe',
      data: { method },
    })
    return camelize<PremiumStatus>(data)
  },

  async cancelPremium(): Promise<PremiumStatus> {
    const data = await request<unknown>({ method: 'POST', url: '/api/eleva/premium/cancel' })
    return camelize<PremiumStatus>(data)
  },

  // ---- Wallet ----

  async getWalletTransactions(limit = 50, offset = 0): Promise<WalletTransactionEntry[]> {
    const data = await request<unknown>({
      url: '/api/eleva/wallet/transactions',
      params: { limit, offset },
    })
    return camelize<WalletTransactionEntry[]>(data)
  },

  // ---- Pages ----

  async getUserPages(userId?: string): Promise<ProfilePageSummary[]> {
    const data = await request<unknown>({
      url: `${PAGES}/my`,
      params: userId ? { user_id: userId } : undefined,
    })
    return camelize<ProfilePageSummary[]>(data)
  },

  async getPage(username: string): Promise<PageDetails> {
    const data = await request<unknown>({ url: `${PAGES}/${username}` })
    return camelize<PageDetails>(data)
  },

  async getPagePosts(
    pageId: string,
    skip = 0,
    take = 21,
  ): Promise<{ data: ProfilePostSummary[]; total: number }> {
    const data = await request<unknown>({
      url: `${PAGES}/${pageId}/posts`,
      params: { skip, take },
    })
    return camelize<{ data: ProfilePostSummary[]; total: number }>(data)
  },

  async togglePageFollow(pageId: string): Promise<boolean> {
    const data = await request<Record<string, unknown>>({
      method: 'POST',
      url: `${PAGES}/${pageId}/follow`,
    })
    return Boolean(data?.is_following ?? data?.isFollowing)
  },

  async createPage(input: {
    name: string
    username: string
    bio?: string
    category?: string
    website?: string
    location?: string
    /** Alpha-2 country; omitted = the server defaults to the owner's country. */
    countryCode?: string
  }): Promise<string> {
    const data = await request<Record<string, unknown>>({
      method: 'POST',
      url: `${PAGES}/create`,
      data: {
        name: input.name,
        username: input.username,
        ...(input.bio ? { bio: input.bio } : {}),
        ...(input.category ? { category: input.category } : {}),
        ...(input.website ? { website: input.website } : {}),
        ...(input.location ? { location: input.location } : {}),
        ...(input.countryCode ? { country_code: input.countryCode } : {}),
      },
    })
    const pageId = data?.page_id ?? data?.pageId
    if (typeof pageId !== 'string') throw new Error('Missing pageId in create-page response')
    return pageId
  },

  /** Edits an existing page - the @username is immutable. Any page admin. */
  async updatePage(
    pageId: string,
    input: {
      name: string
      bio?: string
      category?: string
      website?: string
      location?: string
      countryCode?: string
    },
  ): Promise<void> {
    await request({
      method: 'PUT',
      url: `${PAGES}/${pageId}`,
      data: {
        name: input.name,
        ...(input.bio !== undefined ? { bio: input.bio } : {}),
        ...(input.category !== undefined ? { category: input.category } : {}),
        ...(input.website !== undefined ? { website: input.website } : {}),
        ...(input.location !== undefined ? { location: input.location } : {}),
        ...(input.countryCode !== undefined ? { country_code: input.countryCode } : {}),
      },
    })
  },

  /** Availability of a page handle (Check button in the create form). */
  async checkPageUsername(username: string): Promise<boolean> {
    const data = await request<Record<string, unknown>>({
      url: `${PAGES}/username-check`,
      params: { username },
    })
    return Boolean(data?.available)
  },

  // ---- Ranking / analytics / embassy ----

  async getTopRankings(country?: string | null): Promise<RankingEntry[]> {
    const data = await request<unknown>({
      url: '/api/eleva/wallet/leaderboard',
      params: { limit: 50, ...(country ? { country } : {}) },
    })
    return camelize<RankingEntry[]>(data)
  },

  async getCreatorAnalytics(userId: string, days: number): Promise<AnalyticsData> {
    const data = await request<unknown>({
      url: '/api/eleva/analytics/creator',
      params: { user_id: userId, days },
    })
    return camelize<AnalyticsData>(data)
  },

  async getMyReferrals(): Promise<Referral[]> {
    const data = await request<unknown>({ url: '/api/eleva/wallet/referrals' })
    return camelize<Referral[]>(data)
  },
}
